import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";

/**
 * <RelatedTicketField /> — extracted from JobCardDetail.jsx (Wave 25.16
 * Phase 2 refactor). Picker that resolves the JC.related_support_ticket
 * — supports either an internal ticket id (UUID), a short_id like
 * "ST0001", or a free-text reference like "AppSheet ticket id". When in
 * edit mode + customer is known, suggests open tickets for that
 * customer.
 */
export default function RelatedTicketField({ job, customer, canEdit, onChange }) {
  const [tickets, setTickets] = useState([]);
  const [resolved, setResolved] = useState(null);
  const value = job.related_support_ticket || "";

  useEffect(() => {
    if (!customer?.id) {
      setTickets([]);
      return;
    }
    api.get(`/tickets?customer_id=${customer.id}`)
      .then(({ data }) => {
        // Only show OPEN tickets in the picker (resolved/closed clutter
        // the list). Keep the currently-linked ticket visible even if
        // it's resolved so we don't surprise-clear the JC's selection.
        const closedStatuses = new Set([
          "resolved", "closed", "resolved_via_jc", "resolved_external", "cancelled",
        ]);
        const open = (data || []).filter(
          (t) => !closedStatuses.has(t.status) || t.id === value || t.short_id === value,
        );
        setTickets(open);
      })
      .catch(() => setTickets([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  // Resolve current value to a ticket row (by id or short_id).
  useEffect(() => {
    if (!value) {
      setResolved(null);
      return;
    }
    const inList = tickets.find((x) => x.id === value || x.short_id === value);
    if (inList) {
      setResolved(inList);
      return;
    }
    api.get(`/tickets`)
      .then(({ data }) => {
        const m = (data || []).find((x) => x.id === value || x.short_id === value);
        setResolved(m || { external_only: true, label: value });
      })
      .catch(() => setResolved({ external_only: true, label: value }));
  }, [value, tickets]);

  if (!canEdit) {
    if (!value) return <div className="hv-input mt-1 opacity-70">—</div>;
    if (resolved && !resolved.external_only) {
      return (
        <Link
          to={`/tickets/${resolved.id}`}
          className="hv-input mt-1 inline-block hover:text-rose-300 transition"
          data-testid="job-related-ticket-link"
        >
          <span className="font-mono text-rose-300">#{resolved.short_id || "—"}</span> · {resolved.subject}
        </Link>
      );
    }
    return <div className="hv-input mt-1 opacity-70 font-mono text-xs">{value}</div>;
  }

  return (
    <div className="space-y-1.5">
      <select
        value={value && tickets.find((x) => x.id === value || x.short_id === value) ? value : ""}
        onChange={(e) => onChange(e.target.value || "")}
        className="hv-input mt-1"
        data-testid="job-related-ticket-select"
      >
        <option value="">— Not linked —</option>
        {tickets.map((t) => (
          <option key={t.id} value={t.id}>
            #{t.short_id || t.id.slice(0, 8)} · {t.subject} ({t.status})
          </option>
        ))}
      </select>
      {value && !tickets.find((x) => x.id === value || x.short_id === value) && (
        <div className="text-[10px] text-[#647087] font-mono">Linked external ref: {value}</div>
      )}
    </div>
  );
}
